/**
 * Contextual Help Content Database
 * Contains all help content for each page in the application
 */

import { HelpContent } from "@/types/help-content";

export const helpContentDatabase: Record<string, HelpContent> = {
  "stock-items": {
    pageId: "stock-items",
    title: "Stock Items",
    description: "Manage your inventory items, track stock levels, and configure item details.",
    
    functionality: {
      overview: "The Stock Items page is your central hub for managing all inventory items. View real-time stock levels, configure item details, and perform bulk operations across your entire inventory.",
      keyFeatures: [
        "View and search all stock items with advanced filtering",
        "Track stock levels, costs, and valuations in real-time",
        "Configure item details including SKU, description, and categories",
        "Support for multiple valuation methods (FIFO, Weighted Average, Standard Cost)",
        "Bulk operations: duplicate, delete, and initiate stock movements",
        "Export data to CSV or Excel for reporting",
        "Customizable columns and views",
      ],
      useCases: [
        "Quickly check stock availability before processing orders",
        "Identify low stock items that need reordering",
        "Update pricing and cost information",
        "Perform stock takes and adjustments",
      ],
    },
    
    userGuide: {
      gettingStarted: "Start by creating your first stock item using the '+ New Stock Item' button. Fill in the required details like code, description, and initial stock levels.",
      commonTasks: [
        {
          step: 1,
          title: "Create a New Stock Item",
          description: "Click the '+ New Stock Item' button in the top right corner. Fill in the required fields including stock code, description, category, and initial stock levels.",
          tips: [
            "Use a consistent naming convention for stock codes",
            "Add detailed descriptions to help with searching",
            "Set reorder levels to automate low stock alerts",
          ],
        },
        {
          step: 2,
          title: "Search and Filter Items",
          description: "Use the search bar to find items by code or description. Click 'Advanced Filters' for more specific criteria like category, warehouse, or stock status.",
          tips: [
            "Save frequently used filter combinations",
            "Use the 'Active/Inactive' filter to manage item lifecycle",
            "Combine multiple filters for precise results",
          ],
        },
        {
          step: 3,
          title: "View Item Details",
          description: "Click on any stock item row to open the details drawer. Here you can see complete information including stock levels by location, movement history, and costing details.",
          tips: [
            "Check the 'Stock by Location' tab to see warehouse distribution",
            "Review movement history to understand stock flow patterns",
            "Use the 'Edit' button to update item information",
          ],
        },
        {
          step: 4,
          title: "Perform Bulk Operations",
          description: "Select multiple items using checkboxes, then use the bulk actions menu to duplicate items, initiate stock movements, or start a stock take.",
          tips: [
            "Use 'Select All' to quickly select items on the current page",
            "Bulk duplicate is useful for creating similar items quickly",
            "Bulk stock movements save time when adjusting multiple items",
          ],
        },
        {
          step: 5,
          title: "Export Data",
          description: "Click the 'Export' button to download your stock data in CSV or Excel format. Choose which columns to include in the export.",
          tips: [
            "Export filtered data for specific reports",
            "Use Excel export for advanced analysis with formulas",
            "Schedule regular exports for backup purposes",
          ],
        },
      ],
      tips: [
        "Keep stock codes short but descriptive for easy reference",
        "Regularly review and deactivate obsolete items",
        "Use custom fields to track additional item attributes",
        "Set up ABC classification to prioritise high-value items",
      ],
    },
    
    faq: [
      {
        question: "What valuation methods are supported?",
        answer: "The system supports three valuation methods: FIFO (First In, First Out) assumes the oldest stock is sold first and tracks cost layers. Weighted Average calculates an average cost across all stock purchases. Standard Cost uses a predetermined fixed cost. Your choice affects cost of goods sold and inventory valuation on financial statements.",
      },
      {
        question: "How do I handle items with multiple units of measure?",
        answer: "Set up the base unit of measure for the item, then configure conversion factors in the item details. For example, if you buy in cases but sell in units, set the conversion ratio accordingly.",
      },
      {
        question: "Can I track serial numbers or batch numbers?",
        answer: "Yes! Enable lot/batch/serial tracking in the item settings. Once enabled, you'll be required to specify lot or serial numbers for all stock movements of that item.",
      },
      {
        question: "Why is my stock value different from what I expected?",
        answer: "Stock value is calculated based on your chosen valuation method and the cost of stock on hand. Check the item's costing method, review recent purchase prices, and verify that all stock movements have been recorded correctly.",
      },
      {
        question: "How do I set reorder points and quantities?",
        answer: "Open the item details drawer and navigate to the 'Reordering' section. Set the minimum stock level (reorder point) and the quantity to order when stock falls below this level. The system will alert you when items need reordering.",
      },
    ],
    
    relatedPages: [
      { label: "Stock Movements", pageId: "stock-movements" },
      { label: "Locations", pageId: "warehouses" },
      { label: "Stock Takes", pageId: "stock-takes" },
      { label: "ABC Classification", pageId: "abc-classification" },
    ],
  },

  "dashboard": {
    pageId: "dashboard",
    title: "Dashboard - Command Center",
    description: "Your intelligent command center for actionable insights, prioritised tasks, and real-time inventory alerts.",
    
    functionality: {
      overview: "The Dashboard is your action-oriented command center that prioritises what needs your attention most. It combines real-time KPIs, AI-driven prioritisation, critical stock alerts, and quick actions to help you make faster, better decisions.",
      keyFeatures: [
        "Quick Stats with color-coded KPI cards: Total Stock Value, Active Items, Out of Stock, Low Stock Alerts",
        "Prioritised Action Items with HIGH/MEDIUM/LOW urgency levels and direct action buttons",
        "Critical Stock Alerts showing days until stockout with visual urgency indicators",
        "Recent Activity timeline showing real-time system operations",
        "Quick Actions for one-click access to common tasks (New Stock Item, Create Movement, Receive Goods, Start Stock Take)",
        "System Capabilities showcase highlighting AI forecasting and analytics features",
        "Smart AI-driven prioritization to focus on what matters most",
      ],
      useCases: [
        "Start your day with a prioritised action list - know exactly what to do first",
        "Identify critical stock issues before they impact operations",
        "Monitor team activity and system operations in real-time",
        "Access common tasks with one click instead of navigating through menus",
        "Discover and leverage advanced system capabilities like AI forecasting",
      ],
    },
    
    userGuide: {
      gettingStarted: "The dashboard loads automatically when you log in and shows your most critical action items first. Start by reviewing the Quick Stats, then work through the Prioritised Action Items tab.",
      commonTasks: [
        {
          step: 1,
          title: "Review Quick Stats",
          description: "Check the four color-coded KPI cards at the top: Total Stock Value (green), Active Items (blue), Out of Stock (red), and Low Stock Alerts (yellow). Each card shows the current value and trend indicator.",
          tips: [
            "Red 'Out of Stock' card requires immediate attention",
            "Yellow 'Low Stock Alerts' shows items approaching stockout",
            "Green border indicates positive metrics (Total Stock Value)",
            "Click trend indicators to understand changes over time",
          ],
        },
        {
          step: 2,
          title: "Work Through Action Items",
          description: "The 'Prioritised Action Items' tab shows tasks sorted by urgency (HIGH/MEDIUM/LOW). Each item has a direct action button to resolve it immediately. Start with HIGH priority items first.",
          tips: [
            "HIGH priority items (red badge) need immediate action",
            "Click action buttons like 'Create PO', 'Adjust Stock', or 'Review' to resolve items",
            "Work through items systematically from top to bottom",
            "Action items are AI-prioritised based on business impact",
          ],
        },
        {
          step: 3,
          title: "Address Stock Alerts",
          description: "Switch to the 'Critical Stock Alerts' tab to see items with low or zero stock. Each alert shows days until stockout with color-coded urgency (red for critical, yellow for warning).",
          tips: [
            "Items with 0 days are out of stock - highest priority",
            "Items with 1-7 days need immediate reordering",
            "Click 'Reorder' button to create purchase orders quickly",
            "Use 'View Details' to see full item information and history",
          ],
        },
        {
          step: 4,
          title: "Monitor Recent Activity",
          description: "The 'Recent Activity' tab shows a real-time timeline of system operations including stock movements, goods receipts, and stock takes. Use this to monitor team activity and system changes.",
          tips: [
            "Color-coded badges show activity type (blue for movements, green for receipts)",
            "Timestamps show when each activity occurred",
            "Review activity to understand daily operations flow",
            "Identify patterns in team workflows",
          ],
        },
        {
          step: 5,
          title: "Use Quick Actions",
          description: "The Quick Actions section provides one-click access to common tasks: New Stock Item, Create Movement, Receive Goods, and Start Stock Take. No need to navigate through menus.",
          tips: [
            "Use Quick Actions to save time on frequent tasks",
            "New Stock Item: Add products to inventory quickly",
            "Create Movement: Record stock transfers or adjustments",
            "Receive Goods: Process incoming shipments",
            "Start Stock Take: Begin inventory counting",
          ],
        },
        {
          step: 6,
          title: "Explore System Capabilities",
          description: "The System Capabilities section showcases advanced features like AI Demand Forecasting, Predictive Analytics, and Smart Reordering. Click 'Learn More' to discover how these features can help your business.",
          tips: [
            "AI Demand Forecasting predicts future stock needs",
            "Predictive Analytics identifies trends and patterns",
            "Smart Reordering automates purchase order creation",
            "Explore these features to optimise your inventory management",
          ],
        },
      ],
      tips: [
        "Start each morning by reviewing HIGH priority action items",
        "Address 'Out of Stock' items immediately to prevent lost sales",
        "Use Quick Actions to reduce task completion time from minutes to seconds",
        "Monitor Recent Activity to stay informed about team operations",
        "Leverage AI capabilities to make data-driven decisions",
        "The dashboard is designed to reduce decision time from 3-5 minutes to 30 seconds",
      ],
    },
    
    faq: [
      {
        question: "How are Action Items prioritised?",
        answer: "Action Items are prioritised using AI-driven algorithms that consider multiple factors: business impact (revenue loss, customer satisfaction), urgency (days until stockout, order deadlines), historical patterns, and item value. HIGH priority items typically have immediate business impact, MEDIUM items need attention within 1-3 days, and LOW items are important but not urgent.",
      },
      {
        question: "What does 'Days Until Stockout' mean in Stock Alerts?",
        answer: "Days Until Stockout is calculated based on current stock levels and average daily usage. For example, if you have 50 units in stock and sell 10 units per day on average, you have 5 days until stockout. Items with 0 days are already out of stock. This helps you prioritise reordering based on urgency.",
      },
      {
        question: "Can I customise the Quick Actions?",
        answer: "Currently, Quick Actions are set to the most common tasks across all users. Future updates will allow you to customise these based on your role and preferences. The current actions (New Stock Item, Create Movement, Receive Goods, Start Stock Take) cover 80% of daily inventory tasks.",
      },
      {
        question: "How often does the dashboard data refresh?",
        answer: "Dashboard data refreshes in real-time as transactions occur. The Quick Stats update immediately when stock movements are recorded. Action Items and Stock Alerts recalculate every 5 minutes. You can also manually refresh using the browser refresh button.",
      },
      {
        question: "What's the difference between Action Items and Stock Alerts?",
        answer: "Action Items include all types of tasks that need attention (low stock, pending approvals, overdue orders, etc.) and are prioritised by business impact. Stock Alerts specifically focus on inventory levels and stockout risks. Think of Action Items as your to-do list, and Stock Alerts as your inventory health monitor.",
      },
      {
        question: "How should I use the dashboard in my morning routine?",
        answer: "Best practice: (1) Check Quick Stats for overnight changes, (2) Work through HIGH priority Action Items first, (3) Review Critical Stock Alerts (0-7 days), (4) Check Recent Activity to see what your team accomplished, (5) Use Quick Actions for any immediate tasks. This routine takes 5-10 minutes and ensures you're focused on what matters most.",
      },
      {
        question: "What are the System Capabilities features?",
        answer: "System Capabilities showcase advanced features available in Stock Evo: AI Demand Forecasting uses machine learning to predict future stock needs, Predictive Analytics identifies trends and anomalies, Smart Reordering automates purchase order creation based on reorder points, and Real-time Insights provide instant visibility into inventory health. These features help you move from reactive to proactive inventory management.",
      },
      {
        question: "Why did 'Pending Orders' change to 'Out of Stock'?",
        answer: "Out of Stock is a more critical metric that directly impacts sales and customer satisfaction. It requires immediate action, while pending orders are tracked elsewhere in the system. The dashboard focuses on the most actionable and urgent metrics to help you prioritise effectively.",
      },
      {
        question: "Can I drill down into the KPI cards?",
        answer: "Currently, KPI cards show summary metrics with trend indicators. Future updates will allow you to click on cards to see detailed breakdowns and historical trends. For now, use the Action Items and Stock Alerts tabs to see the detailed data behind these metrics.",
      },
    ],
    
    relatedPages: [
      { label: "Stock Items", pageId: "stock-items" },
      { label: "Stock Movements", pageId: "add-movement" },
      { label: "Purchase Orders", pageId: "purchase-orders" },
      { label: "Stock Takes", pageId: "stock-takes" },
      { label: "Locations", pageId: "warehouses" },
      { label: "Receive Goods", pageId: "receive-goods" },
    ],
  },

  "warehouses": {
    pageId: "warehouses",
    title: "Locations",
    description: "Manage warehouse locations and optimise your storage organisation.",
    
    functionality: {
      overview: "Organise your physical storage with a hierarchical location structure. Track stock by specific locations and optimise picking routes.",
      keyFeatures: [
        "Multi-level location hierarchy (Warehouse > Aisle > Rack > Shelf > Bin)",
        "Location capacity management and utilisation tracking",
        "Picking sequence optimisation for efficient order fulfillment",
        "Stock visibility by location",
        "Location-based stock movements and transfers",
        "Bulk location import and management",
      ],
      useCases: [
        "Set up new warehouse locations systematically",
        "Track which items are stored where",
        "Optimize picking routes to reduce travel time",
        "Monitor location capacity and utilization",
      ],
    },
    
    userGuide: {
      gettingStarted: "Start by creating your main warehouse, then add locations within it using the hierarchical structure (Aisle, Rack, Shelf, Bin).",
      commonTasks: [
        {
          step: 1,
          title: "Create a Warehouse",
          description: "Click '+ New Warehouse' and enter the warehouse name, code, and address details. This becomes the top level of your location hierarchy.",
          tips: [
            "Use clear, consistent naming conventions",
            "Include address details for multi-site operations",
          ],
        },
        {
          step: 2,
          title: "Add Locations",
          description: "Within each warehouse, create locations using the Aisle-Rack-Shelf-Bin structure. Set capacity limits and picking sequences for each location.",
          tips: [
            "Use alphanumeric codes for easy identification (e.g., A01-R02-S03-B04)",
            "Set picking sequences to optimise warehouse flow",
            "Define capacity limits to prevent overstocking",
          ],
        },
        {
          step: 3,
          title: "View Stock by Location",
          description: "Click on any location to see which items are currently stored there and their quantities. Use this for stock takes and location audits.",
          tips: [
            "Regularly review high-utilisation locations",
            "Identify empty locations for better space utilisation",
          ],
        },
        {
          step: 4,
          title: "Transfer Stock Between Locations",
          description: "Use the stock movement function to transfer items between locations. This maintains accurate location-level inventory records.",
          tips: [
            "Always specify both source and destination locations",
            "Use transfer movements for warehouse reorganization",
          ],
        },
      ],
      tips: [
        "Plan your location structure before creating locations in bulk",
        "Use the import function for large warehouse setups",
        "Review location utilisation reports to optimise space usage",
        "Deactivate unused locations rather than deleting them",
      ],
    },
    
    faq: [
      {
        question: "What's the best way to structure my warehouse locations?",
        answer: "Use a logical hierarchy that matches your physical layout. Typically: Warehouse > Aisle > Rack > Shelf > Bin. Keep codes short and consistent. For example: WH01-A05-R03-S02-B01.",
      },
      {
        question: "Can I move stock between warehouses?",
        answer: "Yes, use the 'Transfer' stock movement type to move items between any locations, including different warehouses. The system tracks the full movement history.",
      },
      {
        question: "How do picking sequences work?",
        answer: "Picking sequences determine the order in which locations are visited during order picking. Lower numbers are picked first. Set these based on your warehouse layout to minimize travel time.",
      },
      {
        question: "What happens if I exceed a location's capacity?",
        answer: "The system will warn you when attempting to move stock into a location that would exceed its capacity, but you can override this if needed. Regular capacity monitoring helps prevent overstocking.",
      },
    ],
    
    relatedPages: [
      { label: "Stock Items", pageId: "stock-items" },
      { label: "Stock Movements", pageId: "stock-movements" },
      { label: "Stock Takes", pageId: "stock-takes" },
    ],
  },

  "purchase-orders": {
    pageId: "purchase-orders",
    title: "Purchase Orders",
    description: "Create and manage purchase orders for inventory replenishment.",
    
    functionality: {
      overview: "Streamline your procurement process with comprehensive purchase order management. Create, track, and receive orders from suppliers.",
      keyFeatures: [
        "Create purchase orders with multiple line items",
        "Track order status from draft to completed",
        "Partial receiving and back-order management",
        "Supplier management and history",
        "Cost tracking and budget monitoring",
        "Automated reorder suggestions based on stock levels",
        "Email POs directly to suppliers",
      ],
      useCases: [
        "Reorder stock items that are running low",
        "Track expected deliveries and plan warehouse space",
        "Manage supplier relationships and performance",
        "Monitor purchasing costs and budgets",
      ],
    },
    
    userGuide: {
      gettingStarted: "Create your first purchase order by clicking '+ New Purchase Order', selecting a supplier, and adding items to order.",
      commonTasks: [
        {
          step: 1,
          title: "Create a Purchase Order",
          description: "Click '+ New Purchase Order', select a supplier, add items with quantities and costs, then save as draft or submit for approval.",
          tips: [
            "Use the reorder suggestions to quickly add items that need restocking",
            "Set expected delivery dates to plan warehouse capacity",
            "Add notes for special delivery instructions",
          ],
        },
        {
          step: 2,
          title: "Submit for Approval",
          description: "Once your PO is complete, submit it for approval if required by your workflow. Approved orders can be sent to suppliers.",
          tips: [
            "Attach supporting documents like quotes or requisitions",
            "Review total costs before submitting",
          ],
        },
        {
          step: 3,
          title: "Receive Goods",
          description: "When goods arrive, use the 'Receive Goods' function to record what was delivered. You can receive partial quantities if needed.",
          tips: [
            "Always verify quantities and quality before receiving",
            "Record any discrepancies or damages immediately",
            "Partial receives automatically create back-orders",
          ],
        },
        {
          step: 4,
          title: "Close or Cancel Orders",
          description: "Mark orders as complete when fully received, or cancel orders that won't be fulfilled. This keeps your order list clean and accurate.",
          tips: [
            "Add cancellation reasons for audit purposes",
            "Review open orders regularly to identify delays",
          ],
        },
      ],
      tips: [
        "Set up favorite suppliers for quick PO creation",
        "Use templates for recurring orders",
        "Monitor lead times to improve reorder point calculations",
        "Review supplier performance metrics regularly",
      ],
    },
    
    faq: [
      {
        question: "Can I receive more than the ordered quantity?",
        answer: "Yes, the system allows over-receiving if your settings permit it. You'll see a warning if the received quantity exceeds the ordered amount. This is useful when suppliers send extra units.",
      },
      {
        question: "What happens to costs when I receive goods?",
        answer: "The cost on the PO line becomes the stock cost for those units. If you're using FIFO, each receipt creates a new cost layer. For weighted average, the cost is recalculated based on all stock on hand. For standard cost, the system tracks variances between actual and standard costs.",
      },
      {
        question: "How do I handle partial deliveries?",
        answer: "Use the 'Receive Goods' function and enter only the quantity actually received. The remaining quantity stays on the PO as a back-order until you receive it or cancel the line.",
      },
      {
        question: "Can I edit a purchase order after it's been submitted?",
        answer: "This depends on your workflow settings. Generally, submitted or approved POs require a change request or cancellation. Draft POs can be edited freely.",
      },
    ],
    
    relatedPages: [
      { label: "Receive Goods", pageId: "receive-goods" },
      { label: "Suppliers", pageId: "suppliers" },
      { label: "Stock Items", pageId: "stock-items" },
    ],
  },

  "stock-movements": {
    pageId: "stock-movements",
    title: "Stock Movements",
    description: "Track and manage all inventory movements and transactions.",
    
    functionality: {
      overview: "View a complete audit trail of all stock movements including receipts, issues, transfers, and adjustments. Essential for inventory accuracy and compliance.",
      keyFeatures: [
        "Complete movement history with full audit trail",
        "Multiple movement types: receipts, issues, transfers, adjustments",
        "Filter by date, item, location, or movement type",
        "Lot/batch/serial number tracking",
        "Cost tracking for each movement",
        "Reverse or adjust movements when needed",
        "Export movement data for analysis",
      ],
      useCases: [
        "Investigate stock discrepancies",
        "Audit inventory transactions",
        "Track item movement patterns",
        "Reconcile physical counts with system records",
      ],
    },
    
    userGuide: {
      gettingStarted: "All stock movements are automatically recorded here. Use filters to find specific transactions or review movement patterns.",
      commonTasks: [
        {
          step: 1,
          title: "View Movement History",
          description: "Browse all movements or use filters to find specific transactions. Click on any movement to see full details including who made the change and when.",
          tips: [
            "Use date filters to review movements for specific periods",
            "Filter by movement type to focus on specific transaction types",
            "Export filtered data for external analysis",
          ],
        },
        {
          step: 2,
          title: "Create Manual Adjustments",
          description: "Use the '+ New Movement' button to record manual stock adjustments, transfers, or write-offs. Always include a reason for audit purposes.",
          tips: [
            "Require approval for large adjustments",
            "Use specific adjustment reasons for better reporting",
            "Attach photos or documents to support adjustments",
          ],
        },
        {
          step: 3,
          title: "Reverse a Movement",
          description: "If a movement was recorded in error, use the 'Reverse' function to create an offsetting transaction. This maintains the audit trail.",
          tips: [
            "Always add a reversal reason",
            "Check if the reversal affects other transactions",
            "Consider adjusting instead of reversing when appropriate",
          ],
        },
        {
          step: 4,
          title: "Analyze Movement Patterns",
          description: "Use the movement data to identify trends, such as frequently transferred items or locations with high activity.",
          tips: [
            "Export data to Excel for pivot table analysis",
            "Look for patterns that indicate process improvements",
            "Review movement velocity for ABC classification",
          ],
        },
      ],
      tips: [
        "Always include detailed notes for manual movements",
        "Review movement reports regularly for anomalies",
        "Use consistent reason codes for better reporting",
        "Train staff on proper movement recording procedures",
      ],
    },
    
    faq: [
      {
        question: "What's the difference between an adjustment and a transfer?",
        answer: "An adjustment changes the quantity at a location (increase or decrease) without a corresponding change elsewhere. A transfer moves stock from one location to another, keeping total stock the same.",
      },
      {
        question: "Can I delete a stock movement?",
        answer: "No, movements cannot be deleted to maintain audit integrity. Instead, use the 'Reverse' function to create an offsetting transaction that cancels the original movement.",
      },
      {
        question: "How do I track why stock was adjusted?",
        answer: "Always use the 'Reason' field when creating adjustments. Common reasons include: stock take adjustment, damage, theft, or counting error. You can also add detailed notes.",
      },
      {
        question: "What happens to costs when I move stock?",
        answer: "Transfers don't change costs - they just move stock between locations. Adjustments that increase stock require a cost to be specified. Adjustments that decrease stock remove cost based on your valuation method (FIFO consumes oldest layers first, Weighted Average uses the current average cost).",
      },
    ],
    
    relatedPages: [
      { label: "Stock Items", pageId: "stock-items" },
      { label: "Locations", pageId: "warehouses" },
      { label: "Stock Takes", pageId: "stock-takes" },
    ],
  },

  "stock-movement-history": {
    pageId: "stock-movement-history",
    title: "Stock Movement History",
    description: "View complete movement history and transaction details for a specific stock item.",
    
    functionality: {
      overview: "Track every transaction that has affected this stock item's quantity, location, or cost. Essential for investigating discrepancies, auditing transactions, and understanding stock flow patterns.",
      keyFeatures: [
        "Complete chronological history of all movements",
        "Movement details: type, quantity, location, date, user",
        "Cost tracking for each transaction",
        "Lot/batch/serial number traceability",
        "Filter by date range, movement type, or location",
        "Export movement data for analysis",
        "Drill-down to source documents (POs, SOs, adjustments)",
      ],
      useCases: [
        "Investigate why stock levels changed unexpectedly",
        "Audit trail for compliance and financial reporting",
        "Trace specific lot/serial numbers through the supply chain",
        "Analyze item velocity and turnover patterns",
      ],
    },
    
    userGuide: {
      gettingStarted: "The Movement History tab shows all transactions for the current stock item in reverse chronological order (newest first).",
      commonTasks: [
        {
          step: 1,
          title: "View Movement Details",
          description: "Each row shows a movement with date, type, quantity change, location, and the user who recorded it. Click any row to see full transaction details.",
          tips: [
            "Positive quantities indicate stock increases (receipts, adjustments in)",
            "Negative quantities indicate stock decreases (issues, sales, adjustments out)",
            "Check the 'Balance' column to see running stock levels",
          ],
        },
        {
          step: 2,
          title: "Filter Movement History",
          description: "Use date filters to focus on specific time periods. Filter by movement type to see only receipts, issues, transfers, or adjustments.",
          tips: [
            "Filter by location to see movements at specific warehouses",
            "Use date ranges to investigate specific incidents",
            "Combine filters for precise analysis",
          ],
        },
        {
          step: 3,
          title: "Trace Lot/Serial Numbers",
          description: "For tracked items, see which lot/batch or serial numbers were involved in each movement. Follow the trail from receipt to sale.",
          tips: [
            "Use lot tracing for recall investigations",
            "Verify FIFO/FEFO compliance",
            "Check expiry dates on lot movements",
          ],
        },
        {
          step: 4,
          title: "Access Source Documents",
          description: "Click the document reference to view the original purchase order, sales order, or adjustment that created the movement.",
          tips: [
            "Review source documents to understand context",
            "Check approval status and authorization",
            "Verify supporting documentation",
          ],
        },
        {
          step: 5,
          title: "Export Movement Data",
          description: "Export the movement history to Excel or CSV for detailed analysis, reporting, or archiving.",
          tips: [
            "Export filtered data for specific analysis",
            "Use Excel pivot tables to analyze patterns",
            "Archive historical data for compliance",
          ],
        },
      ],
      tips: [
        "Review movement history when investigating stock discrepancies",
        "Look for unusual patterns like frequent adjustments",
        "Check that movement dates align with business processes",
        "Verify that high-value movements have proper authorization",
      ],
    },
    
    faq: [
      {
        question: "Why don't I see all movements for this item?",
        answer: "Check your date filter - it may be limiting the results. Also verify you have permission to view movements at all locations. Some movements may be in draft status and not yet posted.",
      },
      {
        question: "What do the different movement types mean?",
        answer: "Receipt: stock coming in (from PO or production). Issue: stock going out (to SO or production). Transfer: moving between locations. Adjustment: manual correction of quantities. Each type has different business rules and authorization requirements.",
      },
      {
        question: "How is the cost shown for each movement?",
        answer: "For receipts, the cost is from the purchase order or manual entry. For issues, the cost depends on your valuation method: FIFO uses the cost of the oldest stock, Weighted Average uses the current average cost, Standard Cost uses the predetermined cost. Cost variances may be shown separately.",
      },
      {
        question: "Can I reverse a movement from this screen?",
        answer: "Yes, if you have appropriate permissions. Select the movement and click 'Reverse' to create an offsetting transaction. Always add a reversal reason for audit purposes. Note that some movements (like those from posted invoices) may require additional steps to reverse.",
      },
      {
        question: "What's the difference between the movement date and the transaction date?",
        answer: "The movement date is when the physical movement occurred. The transaction date is when it was recorded in the system. These should normally be the same, but backdated entries may show differences. Large discrepancies may indicate process issues.",
      },
    ],
    
    relatedPages: [
      { label: "Stock Items", pageId: "stock-items" },
      { label: "Stock Movements", pageId: "stock-movements" },
      { label: "Lot/Batch/Serial Tracking", pageId: "lot-batch-serial" },
    ],
  },

  "stock-takes-home": {
    pageId: "stock-takes-home",
    title: "Stock Takes - Getting Started",
    description: "Choose the right type of stock count for your needs.",
    
    functionality: {
      overview: "Stock takes (also called inventory counts or physical counts) help you verify that your system quantities match what's actually in your warehouse. Choose from three purpose-driven counting methods based on your needs.",
      keyFeatures: [
        "Quick Count: Complete inventory count of all items",
        "Targeted Count: Count specific items or locations you choose",
        "Scheduled Counts: ABC-based recurring cycle counts",
        "Mobile-friendly counting interface (coming soon)",
        "Barcode scanning support (coming soon)",
        "Real-time variance detection",
        "Automated adjustment posting",
      ],
      useCases: [
        "Year-end audits and compliance requirements (Quick Count)",
        "Spot checks and discrepancy investigations (Targeted Count)",
        "Continuous accuracy with cycle counting (Scheduled Counts)",
        "Location-specific audits and reorganization",
      ],
    },
    
    userGuide: {
      gettingStarted: "Choose the counting method that best fits your needs: Quick Count for complete inventory, Targeted Count for specific items/locations, or Scheduled Counts for ongoing cycle counting.",
      commonTasks: [
        {
          step: 1,
          title: "Choose Your Counting Method",
          description: "Select from three options: Quick Count (all items), Targeted Count (specific items/locations), or Scheduled Counts (ABC-based recurring).",
          tips: [
            "Use Quick Count for year-end audits",
            "Use Targeted Count for 80% of your counting needs",
            "Set up Scheduled Counts for continuous accuracy",
          ],
        },
        {
          step: 2,
          title: "Quick Count Setup",
          description: "For complete inventory counts, select the warehouse, assign a counter, and set the count date. All items are automatically included.",
          tips: [
            "Schedule during low-activity periods",
            "Assign experienced counters for full counts",
            "Plan for adequate time to complete",
          ],
        },
        {
          step: 3,
          title: "Targeted Count Setup",
          description: "Choose whether to select specific items or count by location. Filter by category, value, or location to find what you need to count.",
          tips: [
            "Use location-based counting for zone audits",
            "Filter by high-value items for focused counts",
            "Perfect for investigating discrepancies",
          ],
        },
        {
          step: 4,
          title: "Scheduled Counts Setup",
          description: "Configure ABC-based cycle counting with automatic scheduling. High-value items are counted more frequently than low-value items.",
          tips: [
            "Requires ABC classification to be set up first",
            "Reduces counting effort by up to 66%",
            "Maintains continuous accuracy",
          ],
        },
      ],
      tips: [
        "Targeted Counts are the most flexible and commonly used",
        "Mobile app with barcode scanning is coming soon",
        "Perform counts during low-activity periods",
        "Investigate all discrepancies promptly",
        "Use Scheduled Counts for ongoing accuracy",
      ],
    },
    
    faq: [
      {
        question: "Which counting method should I use?",
        answer: "Use Quick Count for year-end audits or compliance requirements when you need to count everything. Use Targeted Count (most common) for spot checks, investigations, or counting specific items/locations. Use Scheduled Counts for ongoing cycle counting based on ABC classification.",
      },
      {
        question: "What's the difference between counting by items vs by location?",
        answer: "In Targeted Count, you can choose to manually select specific items to count, or select entire locations and count all items in those locations. Location-based counting is faster for zone audits and reorganization.",
      },
      {
        question: "How do Scheduled Counts work?",
        answer: "Scheduled Counts use ABC classification to automatically schedule recurring counts. High-value A items are counted monthly, B items quarterly, and C items annually. This reduces counting effort while maintaining accuracy where it matters most.",
      },
      {
        question: "When will the mobile app be available?",
        answer: "The mobile app with barcode scanning is currently in development. All counting pages are being designed with mobile-first principles to ensure a great experience when it launches.",
      },
      {
        question: "Can I pause a count and resume later?",
        answer: "Yes, all count types support saving progress and resuming later. This is especially useful for large counts that span multiple days or shifts.",
      },
    ],
    
    relatedPages: [
      { label: "ABC Classification", pageId: "abc-classification" },
      { label: "Stock Items", pageId: "stock-items" },
      { label: "Stock Movements", pageId: "stock-movements" },
      { label: "Locations", pageId: "warehouses" },
    ],
  },

  "stock-takes": {
    pageId: "stock-takes",
    title: "Stock Takes",
    description: "Perform physical stock counts and reconcile with system records.",
    
    functionality: {
      overview: "Conduct accurate stock takes to verify physical inventory against system records. Support for full, partial, and cycle counting methods.",
      keyFeatures: [
        "Multiple stock take types: full warehouse, partial, cycle count, ABC-based",
        "Mobile-friendly counting interface",
        "Blind counting option to prevent bias",
        "Variance detection and approval workflows",
        "Automatic adjustment posting",
        "Historical stock take records and analysis",
        "Export count sheets and variance reports",
      ],
      useCases: [
        "Year-end physical inventory counts",
        "Regular cycle counting programs",
        "Investigate and resolve stock discrepancies",
        "Compliance with audit requirements",
      ],
    },
    
    userGuide: {
      gettingStarted: "Create a new stock take by selecting the type, warehouse, and items to count. Generate count sheets and begin counting.",
      commonTasks: [
        {
          step: 1,
          title: "Create a Stock Take",
          description: "Click '+ New Stock Take', select the type (full, partial, cycle count), choose the warehouse and items, then save as draft.",
          tips: [
            "Use ABC classification for cycle counting priorities",
            "Schedule stock takes during low-activity periods",
            "Enable blind counting to improve accuracy",
          ],
        },
        {
          step: 2,
          title: "Generate Count Sheets",
          description: "Print or export count sheets for your team. These show which items to count and their locations.",
          tips: [
            "Organise count sheets by location for efficiency",
            "Use barcode scanners to speed up counting",
            "Assign specific areas to different counters",
          ],
        },
        {
          step: 3,
          title: "Enter Count Results",
          description: "Record the physical quantities counted. The system automatically calculates variances against expected quantities.",
          tips: [
            "Double-count items with large variances",
            "Investigate variances before posting adjustments",
            "Add notes explaining significant differences",
          ],
        },
        {
          step: 4,
          title: "Review and Post Adjustments",
          description: "Review all variances, approve adjustments, then post to update system quantities. This creates stock movement records.",
          tips: [
            "Set variance thresholds requiring manager approval",
            "Review high-value variances carefully",
            "Post adjustments promptly to maintain accuracy",
          ],
        },
      ],
      tips: [
        "Conduct regular cycle counts instead of annual full counts",
        "Use ABC classification to prioritise counting frequency",
        "Train counters on proper counting procedures",
        "Investigate recurring variances to identify root causes",
      ],
    },
    
    faq: [
      {
        question: "What's the difference between a full and targeted count?",
        answer: "A full stock take counts all items in a warehouse, typically done annually. A targeted count counts selected items, categories, or locations, useful for cycle counting programs, location audits, or investigating specific discrepancies.",
      },
      {
        question: "What is blind counting and when should I use it?",
        answer: "Blind counting hides the expected quantity from counters, preventing bias. Counters record only what they physically see. This improves accuracy but may take longer. Use it for high-value items or when accuracy is critical.",
      },
      {
        question: "How do I handle items found in unexpected locations?",
        answer: "Record the found quantity in the actual location during the count. After posting, use a transfer movement to relocate the stock to the correct location if needed.",
      },
      {
        question: "Can I pause a stock take and resume later?",
        answer: "Yes, save the stock take as 'In Progress'. All entered counts are saved and you can continue later. This is useful for large counts spanning multiple days.",
      },
      {
        question: "What happens to stock movements during a stock take?",
        answer: "Ideally, freeze stock movements in the area being counted. If movements must occur, note them and adjust the count accordingly. Some systems can track movements during the count period.",
      },
    ],
    
    relatedPages: [
      { label: "Stock Items", pageId: "stock-items" },
      { label: "Stock Movements", pageId: "stock-movements" },
      { label: "Locations", pageId: "warehouses" },
      { label: "ABC Classification", pageId: "abc-classification" },
    ],
  },

  "lot-batch-serial": {
    pageId: "lot-batch-serial",
    title: "Lot/Batch/Serial Tracking",
    description: "Track inventory by lot numbers, batch codes, or serial numbers for traceability.",
    
    functionality: {
      overview: "Maintain complete traceability of inventory items through lot, batch, or serial number tracking. Essential for regulated industries and quality control.",
      keyFeatures: [
        "Lot/batch number tracking for groups of items",
        "Serial number tracking for individual items",
        "Expiry date management and alerts",
        "Full traceability from receipt to sale",
        "Recall management capabilities",
        "FEFO (First Expired, First Out) support",
        "Compliance reporting for audits",
      ],
      useCases: [
        "Track pharmaceutical products with batch numbers and expiry dates",
        "Manage electronics with serial numbers for warranty tracking",
        "Food industry compliance with traceability requirements",
        "Product recall management and investigation",
      ],
    },
    
    userGuide: {
      gettingStarted: "Enable lot/batch/serial tracking on stock items that require it. Then specify lot/batch/serial numbers for all movements of those items.",
      commonTasks: [
        {
          step: 1,
          title: "Enable Tracking on Items",
          description: "In the stock item settings, enable lot/batch or serial number tracking. Choose the appropriate tracking method for each item.",
          tips: [
            "Use lot/batch tracking for items produced or received in groups",
            "Use serial tracking for unique, high-value items",
            "Enable expiry date tracking for perishable items",
          ],
        },
        {
          step: 2,
          title: "Record Lot/Serial Numbers on Receipt",
          description: "When receiving goods, enter the lot/batch number or serial numbers. Include expiry dates if applicable.",
          tips: [
            "Scan barcodes to capture lot/serial numbers quickly",
            "Verify expiry dates match supplier documentation",
            "Keep supplier lot numbers for traceability",
          ],
        },
        {
          step: 3,
          title: "Issue Stock by Lot/Serial",
          description: "When issuing stock, select which lot/batch or serial numbers to use. The system can suggest FIFO or FEFO order.",
          tips: [
            "Use FEFO for items with expiry dates",
            "Reserve specific serial numbers for customer orders",
            "Check expiry dates before issuing",
          ],
        },
        {
          step: 4,
          title: "Track and Report",
          description: "View lot/serial number history, check current locations, and generate traceability reports for audits or recalls.",
          tips: [
            "Run regular expiry date reports to identify aging stock",
            "Maintain lot/serial number records for required retention periods",
            "Test recall procedures periodically",
          ],
        },
      ],
      tips: [
        "Establish clear lot numbering conventions",
        "Train staff on the importance of accurate lot/serial recording",
        "Use automated data capture (barcodes, RFID) when possible",
        "Regularly audit lot/serial number accuracy",
      ],
    },
    
    faq: [
      {
        question: "What's the difference between lot and serial number tracking?",
        answer: "Lot/batch tracking groups multiple items under one identifier (e.g., production batch 12345 contains 1000 units). Serial tracking assigns a unique identifier to each individual item (e.g., laptop serial number ABC123). Use lots for bulk items, serials for unique items.",
      },
      {
        question: "Can I change lot/serial numbers after they're recorded?",
        answer: "Generally no, to maintain traceability integrity. If a lot/serial was recorded incorrectly, reverse the movement and create a new one with the correct information. Document the reason for the change.",
      },
      {
        question: "How does FEFO differ from FIFO?",
        answer: "FIFO (First In, First Out) issues the oldest stock first based on receipt date. FEFO (First Expired, First Out) issues stock with the nearest expiry date first. Use FEFO for perishable items to minimize waste.",
      },
      {
        question: "What information should I include with a lot number?",
        answer: "At minimum: lot/batch number, receipt date, and quantity. Additionally consider: expiry date, manufacture date, supplier lot number, and any quality control test results.",
      },
      {
        question: "How do I handle a product recall?",
        answer: "Use the lot/serial traceability report to identify all affected items. The system shows where the lot is currently located and its complete movement history, including which customers received items from that lot.",
      },
    ],
    
    relatedPages: [
      { label: "Stock Items", pageId: "stock-items" },
      { label: "Stock Movements", pageId: "stock-movements" },
      { label: "Purchase Orders", pageId: "purchase-orders" },
      { label: "Sales Orders", pageId: "sales-orders" },
    ],
  },

  "suppliers": {
    pageId: "suppliers",
    title: "Suppliers",
    description: "Manage supplier information and track supplier performance.",
    
    functionality: {
      overview: "Maintain a comprehensive supplier database with contact details, payment terms, and performance metrics.",
      keyFeatures: [
        "Complete supplier contact and banking information",
        "Payment terms and credit limit management",
        "Supplier performance tracking and ratings",
        "Purchase history and spend analysis",
        "Lead time tracking for better planning",
        "Supplier documents and certifications",
        "Multi-currency support",
      ],
      useCases: [
        "Maintain accurate supplier contact information",
        "Track supplier performance and reliability",
        "Analyze purchasing patterns and costs",
        "Manage supplier relationships and negotiations",
      ],
    },
    
    userGuide: {
      gettingStarted: "Add suppliers by clicking '+ New Supplier' and entering their details. Link suppliers to stock items for automated reordering.",
      commonTasks: [
        {
          step: 1,
          title: "Add a New Supplier",
          description: "Click '+ New Supplier', enter company name, contact details, payment terms, and banking information.",
          tips: [
            "Use a consistent supplier code format",
            "Record multiple contacts for different departments",
            "Set default payment terms to speed up PO creation",
          ],
        },
        {
          step: 2,
          title: "Link Suppliers to Stock Items",
          description: "In stock item details, specify the preferred supplier and any alternative suppliers. Include supplier part numbers and lead times.",
          tips: [
            "Maintain at least one alternative supplier for critical items",
            "Record accurate lead times for reorder point calculations",
            "Update supplier part numbers when they change",
          ],
        },
        {
          step: 3,
          title: "Track Supplier Performance",
          description: "Review supplier metrics including on-time delivery rate, quality issues, and price competitiveness.",
          tips: [
            "Set performance targets for key suppliers",
            "Conduct regular supplier reviews",
            "Address performance issues promptly",
          ],
        },
        {
          step: 4,
          title: "Manage Supplier Documents",
          description: "Upload and maintain supplier certificates, insurance documents, contracts, and quality agreements.",
          tips: [
            "Set reminders for document expiry dates",
            "Maintain current insurance certificates",
            "Keep signed contracts and amendments",
          ],
        },
      ],
      tips: [
        "Regularly update supplier contact information",
        "Negotiate better terms with high-volume suppliers",
        "Diversify suppliers to reduce risk",
        "Build strong relationships with key suppliers",
      ],
    },
    
    faq: [
      {
        question: "How do I set a preferred supplier for a stock item?",
        answer: "In the stock item details, go to the Suppliers tab and mark one supplier as 'Preferred'. This supplier will be automatically selected when creating purchase orders for that item.",
      },
      {
        question: "Can I have different prices from the same supplier?",
        answer: "Yes, you can set up price breaks based on quantity or maintain a price history. The system can use the most recent price or quantity-based pricing when creating POs.",
      },
      {
        question: "How is supplier performance calculated?",
        answer: "Performance metrics typically include: on-time delivery percentage, quality acceptance rate, price competitiveness, and responsiveness. You can weight these factors based on your priorities.",
      },
      {
        question: "What should I do with inactive suppliers?",
        answer: "Mark suppliers as inactive rather than deleting them to preserve historical data. Inactive suppliers won't appear in selection lists but their history remains accessible for reporting.",
      },
    ],
    
    relatedPages: [
      { label: "Purchase Orders", pageId: "purchase-orders" },
      { label: "Stock Items", pageId: "stock-items" },
      { label: "Receive Goods", pageId: "receive-goods" },
    ],
  },

  "sales-orders": {
    pageId: "sales-orders",
    title: "Sales Orders",
    description: "Create and manage customer sales orders and track fulfillment.",
    
    functionality: {
      overview: "Process customer orders efficiently from creation through fulfillment. Track order status, allocate stock, and manage deliveries.",
      keyFeatures: [
        "Create sales orders with multiple line items",
        "Stock allocation and reservation",
        "Partial fulfillment and back-order management",
        "Order status tracking from quote to delivered",
        "Pricing and discount management",
        "Delivery scheduling and tracking",
        "Integration with stock movements",
      ],
      useCases: [
        "Process customer orders and allocate stock",
        "Track order fulfillment and delivery status",
        "Manage back-orders and partial shipments",
        "Analyze sales patterns and customer demand",
      ],
    },
    
    userGuide: {
      gettingStarted: "Create a sales order by selecting a customer, adding items, and confirming stock availability. Process the order through picking, packing, and shipping.",
      commonTasks: [
        {
          step: 1,
          title: "Create a Sales Order",
          description: "Click '+ New Sales Order', select a customer, add items with quantities and prices, then save or submit for processing.",
          tips: [
            "Check stock availability before confirming orders",
            "Apply customer-specific pricing and discounts automatically",
            "Set expected delivery dates based on stock availability",
          ],
        },
        {
          step: 2,
          title: "Allocate Stock",
          description: "Allocate (reserve) stock for the order to prevent overselling. The system can auto-allocate based on FIFO or location rules.",
          tips: [
            "Allocate stock as soon as orders are confirmed",
            "Use location-based allocation to optimise picking",
            "Monitor allocated vs available stock levels",
          ],
        },
        {
          step: 3,
          title: "Pick and Pack Orders",
          description: "Generate picking lists, pick items from warehouse locations, then pack for shipment. Record serial/lot numbers if required.",
          tips: [
            "Batch pick multiple orders for efficiency",
            "Verify items and quantities during packing",
            "Print shipping labels and packing slips",
          ],
        },
        {
          step: 4,
          title: "Ship and Invoice",
          description: "Record the shipment to update stock levels and create the invoice. Partial shipments are supported for back-ordered items.",
          tips: [
            "Capture tracking numbers for customer service",
            "Send shipping notifications to customers",
            "Handle back-orders promptly",
          ],
        },
      ],
      tips: [
        "Set up customer-specific pricing for regular clients",
        "Use order templates for recurring orders",
        "Monitor order cycle time to identify bottlenecks",
        "Communicate proactively about delays or stock issues",
      ],
    },
    
    faq: [
      {
        question: "What's the difference between allocated and available stock?",
        answer: "Available stock is the physical quantity on hand minus any allocations. Allocated stock is reserved for specific orders but not yet shipped. This prevents overselling while orders are being processed.",
      },
      {
        question: "How do I handle back-orders?",
        answer: "When you can't fulfill the full quantity, ship what's available and leave the remainder as a back-order. The system tracks back-ordered quantities and can automatically fulfill them when stock arrives.",
      },
      {
        question: "Can I edit an order after it's been submitted?",
        answer: "This depends on the order status. Draft orders can be edited freely. Submitted orders may require approval to change. Once picking has started, changes become more restricted to maintain accuracy.",
      },
      {
        question: "How does pricing work for sales orders?",
        answer: "The system uses a pricing hierarchy: customer-specific prices, price list prices, then standard item prices. Discounts can be applied at the line or order level. Prices can be locked when the order is created.",
      },
      {
        question: "What happens to stock when I ship an order?",
        answer: "Shipping creates a stock movement that reduces inventory at the shipping location. If lot/serial tracking is enabled, you must specify which lots/serials are being shipped. The cost of goods sold is calculated based on your valuation method.",
      },
    ],
    
    relatedPages: [
      { label: "Customers", pageId: "customers" },
      { label: "Stock Items", pageId: "stock-items" },
      { label: "Stock Movements", pageId: "stock-movements" },
    ],
  },

  "customers": {
    pageId: "customers",
    title: "Customers",
    description: "Manage customer information and track customer relationships.",
    
    functionality: {
      overview: "Maintain a comprehensive customer database with contact details, credit terms, and sales history.",
      keyFeatures: [
        "Complete customer contact and billing information",
        "Credit terms and credit limit management",
        "Customer-specific pricing and discounts",
        "Sales history and analysis",
        "Delivery addresses and preferences",
        "Customer documents and contracts",
        "Multi-currency support",
      ],
      useCases: [
        "Maintain accurate customer contact information",
        "Manage credit limits and payment terms",
        "Track customer purchase patterns",
        "Provide personalized pricing and service",
      ],
    },
    
    userGuide: {
      gettingStarted: "Add customers by clicking '+ New Customer' and entering their details. Set up pricing and credit terms for each customer.",
      commonTasks: [
        {
          step: 1,
          title: "Add a New Customer",
          description: "Click '+ New Customer', enter company name, contact details, billing address, and payment terms.",
          tips: [
            "Use a consistent customer code format",
            "Record multiple contacts for different departments",
            "Set credit limits to manage risk",
          ],
        },
        {
          step: 2,
          title: "Set Up Customer Pricing",
          description: "Configure customer-specific prices, discounts, or assign to a price list. These prices apply automatically on sales orders.",
          tips: [
            "Use price lists for customer groups with similar pricing",
            "Set up volume discounts for large customers",
            "Review and update pricing regularly",
          ],
        },
        {
          step: 3,
          title: "Manage Delivery Addresses",
          description: "Add multiple delivery addresses for customers with multiple locations. Set a default address for convenience.",
          tips: [
            "Include delivery instructions for each address",
            "Verify addresses to prevent shipping errors",
            "Keep contact information for each location",
          ],
        },
        {
          step: 4,
          title: "Track Customer Activity",
          description: "Review customer sales history, order patterns, and payment behavior. Use this data for better customer service.",
          tips: [
            "Identify your top customers by revenue",
            "Monitor payment patterns and follow up on overdue accounts",
            "Analyze purchase patterns for upselling opportunities",
          ],
        },
      ],
      tips: [
        "Regularly update customer contact information",
        "Segment customers for targeted marketing",
        "Build relationships with key accounts",
        "Respond promptly to customer inquiries",
      ],
    },
    
    faq: [
      {
        question: "How do I set customer-specific pricing?",
        answer: "In the customer details, go to the Pricing tab. You can set specific prices for individual items, apply a discount percentage, or assign the customer to a price list. Customer-specific prices override standard prices on sales orders.",
      },
      {
        question: "What happens when a customer exceeds their credit limit?",
        answer: "The system will warn you when creating orders that would exceed the credit limit. Depending on your settings, you may be able to override this with proper authorization, or the order may be blocked until payment is received.",
      },
      {
        question: "Can I have different payment terms for different customers?",
        answer: "Yes, each customer can have their own payment terms (e.g., Net 30, Net 60, COD). These terms are applied automatically to invoices and affect due dates and aging reports.",
      },
      {
        question: "How do I handle inactive customers?",
        answer: "Mark customers as inactive rather than deleting them to preserve historical data. Inactive customers won't appear in selection lists but their sales history remains accessible for reporting and analysis.",
      },
    ],
    
    relatedPages: [
      { label: "Sales Orders", pageId: "sales-orders" },
      { label: "Stock Items", pageId: "stock-items" },
    ],
  },

  "abc-classification": {
    pageId: "abc-classification",
    title: "ABC Classification",
    description: "Classify inventory items by value and importance for optimised management.",
    
    functionality: {
      overview: "Apply ABC analysis to categorize inventory items based on their value contribution. Focus management attention on high-value items while streamlining processes for low-value items.",
      keyFeatures: [
        "Automatic classification based on value, usage, or custom criteria",
        "Configurable thresholds for A, B, and C categories",
        "Visual dashboards showing classification distribution",
        "Integration with cycle counting and reorder policies",
        "Historical classification tracking",
        "What-if analysis for threshold adjustments",
      ],
      useCases: [
        "Prioritise cycle counting efforts on high-value items",
        "Set different service levels by classification",
        "Optimise inventory investment",
        "Focus purchasing negotiations on A items",
      ],
    },
    
    userGuide: {
      gettingStarted: "Run the ABC classification analysis to categorize your items. Review the results and adjust thresholds if needed.",
      commonTasks: [
        {
          step: 1,
          title: "Run ABC Classification",
          description: "Click 'Run Classification' to analyze your inventory. The system calculates each item's contribution to total value and assigns A, B, or C classification.",
          tips: [
            "Run classification monthly or quarterly",
            "Consider both value and usage frequency",
            "Review items near classification boundaries",
          ],
        },
        {
          step: 2,
          title: "Adjust Classification Thresholds",
          description: "Modify the percentage thresholds for each category. Typical settings: A items = top 20% of value (80% of total), B items = next 30% (15% of total), C items = remaining 50% (5% of total).",
          tips: [
            "Use the 80-20 rule as a starting point",
            "Adjust based on your business needs",
            "Consider industry-specific factors",
          ],
        },
        {
          step: 3,
          title: "Apply Different Policies by Class",
          description: "Set up different management policies for each class: A items get frequent cycle counts and tight controls, C items use simpler processes.",
          tips: [
            "Count A items monthly, B items quarterly, C items annually",
            "Apply stricter approval for A item adjustments",
            "Use two-bin systems for C items",
          ],
        },
        {
          step: 4,
          title: "Monitor and Review",
          description: "Track how classification affects inventory performance. Review and reclassify items as business conditions change.",
          tips: [
            "Watch for items moving between classifications",
            "Investigate unusual classification changes",
            "Adjust policies based on results",
          ],
        },
      ],
      tips: [
        "ABC classification is dynamic - items can move between categories",
        "Consider multiple criteria: value, margin, criticality",
        "Communicate classification policies to your team",
        "Use ABC data for strategic inventory decisions",
      ],
    },
    
    faq: [
      {
        question: "What criteria are used for ABC classification?",
        answer: "Typically, items are classified by their contribution to total inventory value (quantity × cost). However, you can also consider sales value, profit margin, usage frequency, or criticality. The system supports multiple classification methods.",
      },
      {
        question: "How often should I reclassify items?",
        answer: "Run ABC classification quarterly or when significant business changes occur. Items can move between categories as demand patterns, costs, or business priorities change.",
      },
      {
        question: "What percentage of items should be in each category?",
        answer: "A common distribution follows the 80-20 rule: A items (20% of items, 80% of value), B items (30% of items, 15% of value), C items (50% of items, 5% of value). Adjust these percentages based on your business needs.",
      },
      {
        question: "Should I manage all A items the same way?",
        answer: "Not necessarily. Within A items, consider additional factors like demand variability, lead time, and criticality. Some A items may need even tighter controls than others.",
      },
      {
        question: "Can I manually override an item's classification?",
        answer: "Yes, you can manually assign classifications for items that require special treatment due to strategic importance, regulatory requirements, or other business reasons. Document the reason for manual overrides.",
      },
    ],
    
    relatedPages: [
      { label: "Stock Items", pageId: "stock-items" },
      { label: "Stock Takes", pageId: "stock-takes" },
      { label: "Reports", pageId: "reports" },
    ],
  },

  "work-orders": {
    pageId: "work-orders",
    title: "Work Orders",
    description: "Create and manage manufacturing work orders to track production from start to finish.",
    
    functionality: {
      overview: "Work Orders are the heart of your manufacturing operations. They define what to produce, when to produce it, and track all materials, operations, and costs involved in the production process. Work orders link your BOMs and routings to actual production activities.",
      keyFeatures: [
        "Create work orders from BOMs with automatic material and operation lists",
        "Track work order status from planned through completion",
        "Manage material requirements and issue materials to production",
        "Track operations progress and labor time",
        "Monitor actual costs vs estimated costs",
        "Support for priority levels and rush orders",
        "Link to projects and cost centers for job costing",
        "Real-time visibility into production floor status",
      ],
      useCases: [
        "Schedule and execute production runs based on demand",
        "Track material consumption and labor hours for accurate costing",
        "Manage production priorities and rush orders",
        "Monitor work-in-progress inventory",
        "Analyze production efficiency and identify bottlenecks",
      ],
    },
    
    userGuide: {
      gettingStarted: "Create a work order by clicking '+ Create Work Order', selecting the product to manufacture, entering the quantity, and setting the schedule. The system automatically pulls in materials and operations from the BOM.",
      commonTasks: [
        {
          step: 1,
          title: "Create a Work Order",
          description: "Click '+ Create Work Order', select the product, enter the quantity to produce, set start and due dates, and choose the production warehouse. The system loads materials and operations from the active BOM.",
          tips: [
            "Check material availability before creating the work order",
            "Set realistic due dates based on routing times and capacity",
            "Use priority levels to manage production scheduling",
            "Link to projects for job costing if applicable",
          ],
        },
        {
          step: 2,
          title: "Release Work Order to Production",
          description: "Review the work order details, verify material availability, then release it to the production floor. Released orders appear on production schedules and picking lists.",
          tips: [
            "Verify all materials are available or on order",
            "Confirm work center capacity is available",
            "Print work order packets with instructions and material lists",
            "Communicate priority orders to production supervisors",
          ],
        },
        {
          step: 3,
          title: "Issue Materials",
          description: "Issue materials from inventory to the work order as production progresses. This reduces inventory and tracks material consumption against the work order.",
          tips: [
            "Issue materials in batches as needed rather than all at once",
            "Record lot/serial numbers if required for traceability",
            "Document any material substitutions or additional materials used",
            "Track scrap and waste separately for accurate costing",
          ],
        },
        {
          step: 4,
          title: "Track Operation Progress",
          description: "Record when each operation starts and completes. Capture actual labor hours and any issues or delays. This provides real-time visibility into production status.",
          tips: [
            "Record labor time accurately for costing and capacity planning",
            "Note any quality issues or rework requirements",
            "Update operation status regularly for accurate scheduling",
            "Document reasons for delays or extended run times",
          ],
        },
        {
          step: 5,
          title: "Complete Work Order",
          description: "When production is finished, record the actual quantity produced, complete any final operations, and close the work order. This receives the finished goods into inventory.",
          tips: [
            "Verify final quantity matches expectations",
            "Record any scrap or rejected units",
            "Review actual costs vs estimated costs",
            "Document lessons learned for future production runs",
          ],
        },
      ],
      tips: [
        "Use work order templates for frequently produced items",
        "Monitor work orders daily to identify issues early",
        "Track actual vs estimated costs to improve future estimates",
        "Review completed work orders to identify process improvements",
        "Use priority levels effectively - not everything can be urgent",
      ],
    },
    
    faq: [
      {
        question: "What's the difference between work order statuses?",
        answer: "Planned: Work order created but not yet released to production. Released: Approved and ready for production to start. In Progress: Production has started, materials are being consumed. Completed: All operations finished, goods received into inventory. On Hold: Temporarily paused (e.g., waiting for materials or customer approval). Cancelled: Work order will not be completed.",
      },
      {
        question: "How do I handle material shortages?",
        answer: "If materials aren't available, you can: 1) Put the work order on hold until materials arrive, 2) Issue partial materials and produce what you can, 3) Substitute approved alternative materials, or 4) Cancel the work order. Always document the reason and communicate with planning.",
      },
      {
        question: "Can I change a work order after it's been released?",
        answer: "Changes to released work orders should be minimized to maintain accuracy. If changes are necessary (e.g., quantity adjustment, material substitution), document the change reason and get appropriate approval. Some changes may require creating a new work order version.",
      },
      {
        question: "How are work order costs calculated?",
        answer: "Work order costs include: Material costs (actual materials issued), Labor costs (actual hours × labor rates), and Overhead costs (applied based on your costing method). The system compares actual costs to estimated costs from the BOM to calculate variances.",
      },
      {
        question: "What happens if I produce more or less than the ordered quantity?",
        answer: "Record the actual quantity produced when completing the work order. Over-production increases inventory and may indicate process issues. Under-production may require a follow-up work order to complete the quantity. Both scenarios affect cost per unit calculations.",
      },
      {
        question: "How do I track work-in-progress (WIP)?",
        answer: "WIP is tracked through work orders in 'Released' or 'In Progress' status. The value includes materials issued plus labor and overhead applied. Review the work orders list filtered by these statuses to see current WIP value and quantities.",
      },
      {
        question: "Can I link work orders to customer orders?",
        answer: "Yes, work orders can be linked to sales orders for make-to-order production. This provides traceability from customer order through production to delivery. You can also link to projects for job costing.",
      },
    ],
    
    relatedPages: [
      { label: "Bills of Material (BOMs)", pageId: "boms" },
      { label: "Stock Items", pageId: "stock-items" },
      { label: "Stock Movements", pageId: "stock-movements" },
      { label: "Production Schedule", pageId: "production-schedule" },
    ],
  },

  "boms": {
    pageId: "boms",
    title: "Bills of Material (BOMs)",
    description: "Define product structures, component lists, and manufacturing recipes.",
    
    functionality: {
      overview: "Bills of Material (BOMs) define the complete structure of products, listing all components, materials, quantities, and operations required for manufacturing or assembly. BOMs are essential for production planning, cost calculation, and inventory management.",
      keyFeatures: [
        "Create and manage multi-level product structures",
        "Define component quantities and units of measure",
        "Track material costs, labor, and overhead",
        "Support multiple BOM types: Manufacturing, Assembly, Kit, Phantom",
        "Version control for BOM changes",
        "Component availability checking",
        "Cost rollup calculations",
        "Where-used analysis",
      ],
      useCases: [
        "Define product structures for manufactured items",
        "Calculate accurate product costs including materials and labor",
        "Plan material requirements for production",
        "Create assembly instructions for warehouse staff",
        "Manage product kits and bundles",
      ],
    },
    
    userGuide: {
      gettingStarted: "Create your first BOM by clicking '+ New BOM', selecting the parent item you want to build, and adding the components required.",
      commonTasks: [
        {
          step: 1,
          title: "Create a New BOM",
          description: "Click '+ New BOM', select the parent item (the finished product), choose the BOM type, and set the base quantity. Then add components with their quantities and costs.",
          tips: [
            "Start with simple single-level BOMs before creating complex multi-level structures",
            "Use consistent units of measure across components",
            "Set the base quantity to match your standard production batch size",
            "Add detailed descriptions to help production staff",
          ],
        },
        {
          step: 2,
          title: "Add Components",
          description: "For each component, specify the item code, quantity required, unit of measure, and cost. Mark optional components if they're not always included.",
          tips: [
            "Check component availability before finalizing the BOM",
            "Include scrap percentages for materials with waste",
            "Add notes for special handling or assembly instructions",
            "Define substitute components for flexibility",
          ],
        },
        {
          step: 3,
          title: "Define Routing Operations",
          description: "For manufacturing BOMs, add routing operations that define the work centers, setup times, and run times required to produce the item.",
          tips: [
            "Sequence operations in the order they're performed",
            "Include realistic setup and run times for accurate scheduling",
            "Specify labor and overhead rates for cost calculation",
            "Document any special equipment or skills required",
          ],
        },
        {
          step: 4,
          title: "Review and Approve",
          description: "Review the complete BOM including components, routing, and costs. Submit for approval if required by your workflow, then activate the BOM.",
          tips: [
            "Verify all component codes are correct",
            "Check that total costs align with expected product costs",
            "Test the BOM with a pilot production run",
            "Document the effective date for version control",
          ],
        },
      ],
      tips: [
        "Use version numbers to track BOM changes over time",
        "Set expiry dates when replacing old BOMs with new versions",
        "Run cost rollup calculations after updating component costs",
        "Review BOMs regularly to identify cost reduction opportunities",
        "Use phantom BOMs for intermediate assemblies that aren't stocked",
      ],
    },
    
    faq: [
      {
        question: "What's the difference between BOM types?",
        answer: "Manufacturing BOMs include routing operations and are used for items you produce. Assembly BOMs are simpler structures for items you assemble without complex operations. Kits are pre-packaged sets of items sold together. Phantom BOMs represent intermediate assemblies that aren't stocked - their components are consumed directly.",
      },
      {
        question: "How do multi-level BOMs work?",
        answer: "Multi-level BOMs contain components that are themselves assemblies with their own BOMs. The system can 'explode' these to show all raw materials needed, or 'implode' to show where a component is used. This is useful for complex products with sub-assemblies.",
      },
      {
        question: "How are BOM costs calculated?",
        answer: "Total cost includes material costs (sum of all component costs), labor costs (from routing operations based on time and rates), and overhead costs (applied based on your costing method). The system automatically calculates cost per unit based on the base quantity.",
      },
      {
        question: "Can I have multiple BOMs for the same item?",
        answer: "Yes, you can have multiple versions of a BOM for the same parent item. Use version numbers and effective dates to manage changes. Only one version should be active at a time, though you can overlap dates for transition periods.",
      },
      {
        question: "What happens when component costs change?",
        answer: "When component costs change, you can run a cost rollup to recalculate the BOM's total cost. This helps you understand the impact of price changes on your product costs. You may want to create a new BOM version if costs change significantly.",
      },
      {
        question: "How do I handle optional components?",
        answer: "Mark components as optional when they're not always included in every production run. Optional components are included in cost calculations but can be excluded when creating production orders. This is useful for customizable products.",
      },
    ],
    
    relatedPages: [
      { label: "Stock Items", pageId: "stock-items" },
      { label: "Assembly", pageId: "assembly" },
      { label: "Work Orders", pageId: "work-orders" },
    ],
  },
};

/**
 * Get help content for a specific page
 */
export function getHelpContent(pageId: string): HelpContent | null {
  return helpContentDatabase[pageId] || null;
}

/**
 * Get all available help page IDs
 */
export function getAllHelpPageIds(): string[] {
  return Object.keys(helpContentDatabase);
}
